import {
  AddressPicker,
  Flexbox, Form,
  PageHeader, Styles,
} from '@/components'
import LowIncomePicker from '@/components/LowIncomePicker'
import usePageTitle from '@/hooks/usePageTitle'
import yup from '@/lib/yupPt'
import manualCastrations from '@/services/api/manualCastrations'
import React, { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import TextareaAutosize from 'react-textarea-autosize'
import { toast } from 'react-toastify'
import { validateCPF } from 'validations-br'
import { cnpj } from '@/lib/validators'
import AnimalForm from './AnimalForm'
import TutorForm from './TutorForm'

const now = new Date()

const schema = yup.object().shape({
  attachmentURL: yup.string().url().trim(),
  fullName: yup.string().trim().required(),
  email: yup.string().email().trim().required(),
  documentNumber: yup.string().test(
    'is-document-number',
    'Documento inválido',
    (value = '') => (value?.length < 12 ? validateCPF(value) : cnpj(value)),
  ).required(),
  phoneNumber: yup
    .string()
    .min(10)
    .max(11)
    .trim()
    .required(),
  microchip: yup.string(),
  castratedAt: yup.date().max(now).required(),
  description: yup.string().trim(),
  terms: yup.string().trim(),
  obs: yup.string().trim(),
  animalName: yup.string().trim().required(),
  animalColor: yup.string().trim().required(),
  animalSize: yup.string().trim().required(),
  animalGender: yup.string().trim().required(),
  animalBornedAt: yup.date().max(new Date()),
  animalBreed: yup.mixed().required(),
  animalCategory: yup.mixed().required(),
  address: yup.object().shape({
    zipcode: yup.string().trim().required().length(8),
    city: yup.object().shape({
      id: yup.number().required(),
      name: yup.string(),
      uf: yup.string(),
    }),
    street: yup.string().trim().required(),
    number: yup.string().trim().required(),
    complement: yup.string().trim(),
    reference: yup.string().trim(),
  }).required(),
  lowIncome: yup.mixed(),
})
function FormManualCastration({ title }) {
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  usePageTitle({ title })

  const onSubmit = useCallback(async (values) => {
    try {
      setLoading(true)
      await manualCastrations.create(values)
      toast.success('Castração manual criada com sucesso')
      history.replace('/app/manual-castrations')
    } catch (e) {
      toast.error(e?.message)
    } finally {
      setLoading(false)
    }
  }, [history])

  return (
    <Styles.Container className="grid-xl">
      <PageHeader
        icon="fas fa-book-medical"
        title="Nova castração manual"
        subtitle="Criação para registro de castração manual"
      />
      <fieldset disabled={loading}>
        <Form.Formik
          initialValues={{
            attachmentURL: '',
            fullName: '',
            email: '',
            documentNumber: '',
            microchip: '',
            phoneNumber: '',
            castratedAt: now,
            description: '',
            terms: '',
            animalName: '',
            animalColor: '',
            animalSize: '',
            animalGender: '',
            animalBornedAt: '',
            animalBreed: '',
            animalCategory: '',
            address: null,
            lowIncome: null,
          }}
          validationSchema={schema}
          onSubmit={onSubmit}
          validateOnMount
        >
          {
            ({
              errors, touched, isValid, values, setFieldValue,
            }) => (
              <Form.ValidationForm>
                <Flexbox.Columns>
                  <Flexbox.Column className="col-8 col-sm-12">
                    <Flexbox.Column className="col-12 divider text-center" data-content="Dados do tutor" />
                    <Flexbox.Columns>
                      <TutorForm touched={touched} errors={errors} setFieldValue={setFieldValue} />
                      <Flexbox.Column className="col-12 divider text-center" data-content="Endereço" />
                      <Flexbox.Column className="col-12">
                        <Form.Group className={`${touched?.address && errors?.address ? 'has-error' : ''}`}>
                          <Form.Label>Endereço <span className="text-error">*</span></Form.Label>
                          <Form.ValidationField name="address">
                            {
                              ({ field }) => (
                                <AddressPicker
                                  address={field.value}
                                  onChange={(address) => setFieldValue('address', address)}
                                />
                              )
                            }
                          </Form.ValidationField>
                        </Form.Group>
                      </Flexbox.Column>
                      <Flexbox.Column className="col-12 divider text-center" data-content="Dados de baixa renda" />
                      <Flexbox.Column className="col-12">
                        <Form.Group className={`${touched?.lowIncome && errors?.lowIncome ? 'has-error' : ''}`}>
                          <Form.Label>Dados de baixa renda</Form.Label>
                          <Form.ValidationField name="lowIncome">
                            {
                              ({ field }) => (
                                <LowIncomePicker
                                  lowIncome={field.value}
                                  onChange={(lowIncome) => setFieldValue('lowIncome', lowIncome)}
                                />
                              )
                            }
                          </Form.ValidationField>
                        </Form.Group>
                      </Flexbox.Column>
                      <Flexbox.Column className="col-12 divider text-center" data-content="Dados do animal" />
                      <AnimalForm
                        touched={touched}
                        errors={errors}
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                      <Flexbox.Column className="col-12 divider text-center" data-content="Detalhes da castração" />
                      <Flexbox.Column className="col-12">
                        <Form.Group className={`${touched?.castratedAt && errors?.castratedAt ? 'has-error' : ''}`}>
                          <Form.Label htmlFor="castratedAt">Data da castração <span className="text-error">*</span></Form.Label>
                          <Form.ValidationField
                            name="castratedAt"
                            id="castratedAt"
                          >
                            {
                              ({ field }) => (
                                <Form.DatePickerField
                                  locale="pt-BR"
                                  name="castratedAt"
                                  placeholderText="DD/MM/AAAA"
                                  onChange={(value) => setFieldValue('castratedAt', value)}
                                  selected={field.value}
                                  maxDate={new Date()}
                                  dateFormat="dd/MM/yyyy HH:mm"
                                  showTimeSelect
                                  className="form-input"
                                />
                              )
                            }
                          </Form.ValidationField>
                          {
                            touched?.castratedAt && errors?.castratedAt
                              ? <Form.Hint className="text-error">{errors.castratedAt}</Form.Hint> : null
                          }
                        </Form.Group>
                      </Flexbox.Column>
                      <Flexbox.Column className="col-12">
                        <Form.Group className={`${touched?.terms && errors?.terms ? 'has-error' : ''}`}>
                          <Form.Label htmlFor="terms">Termos da castração</Form.Label>
                          <Form.ValidationField
                            name="terms"
                            id="terms"
                          >
                            {
                              ({ field }) => (
                                <>
                                  <TextareaAutosize
                                    name="terms"
                                    style={{ resize: 'none' }}
                                    className="form-input"
                                    rows={1}
                                    maxRows={3}
                                    placeholder="Termos da castração..."
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...field}
                                  />

                                  {
                                    touched?.terms && errors?.terms
                                      ? <Form.Hint className="text-error">{errors.terms}</Form.Hint> : null
                                  }
                                </>
                              )
                            }
                          </Form.ValidationField>
                        </Form.Group>
                      </Flexbox.Column>
                      <Flexbox.Column className="col-12">
                        <Form.Group className={`${touched?.obs && errors?.obs ? 'has-error' : ''}`}>
                          <Form.Label htmlFor="obs">Observações</Form.Label>
                          <Form.ValidationField
                            name="obs"
                            id="obs"
                          >
                            {
                              ({ field }) => (
                                <>
                                  <TextareaAutosize
                                    name="obs"
                                    style={{ resize: 'none' }}
                                    className="form-input"
                                    rows={1}
                                    maxRows={3}
                                    placeholder="Observações..."
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...field}
                                  />

                                  {
                                    touched?.obs && errors?.obs
                                      ? <Form.Hint className="text-error">{errors.obs}</Form.Hint> : null
                                  }
                                </>
                              )
                            }
                          </Form.ValidationField>
                        </Form.Group>
                      </Flexbox.Column>
                      <Flexbox.Column className="col-12 divider" />
                      <Flexbox.Column className="col-12 text-right">
                        <button type="submit" className={`btn btn-primary ${loading ? 'loading' : ''}`} disabled={!isValid}>
                          <i className="fas fa-check" />
                          &nbsp;Confirmar
                        </button>
                      </Flexbox.Column>
                    </Flexbox.Columns>
                  </Flexbox.Column>
                </Flexbox.Columns>
              </Form.ValidationForm>
            )
          }
        </Form.Formik>

      </fieldset>

    </Styles.Container>
  )
}

export default FormManualCastration
